import { useEffect, useMemo, useState } from 'react'
import { OnboardingState } from '../../hooks/use-onboarding-setup'
import { OnboardingStepKey, OnboardingStepStatus } from '@cbgms/api'
import { Loader } from '@carsys/ui'

// useShouldRunInBackground return whether or not the step should run in the background.
// Steps that run in the background will attempt to submit immediately.
// If successful the step is skipped, if failed it will allow the user to patch the form-data
export const useShouldRunInBackground = (step: OnboardingStepKey, onboardingInfo: OnboardingState): boolean => {
  return useMemo(() => {
    const matchedStep = onboardingInfo.stepData.find(s => s.Key === step)
    return shouldRunInBackground(matchedStep?.State || OnboardingStepStatus.Off)
  }, [step, onboardingInfo.stepData])
}

const shouldRunInBackground = (status: OnboardingStepStatus) => {
  if (status === OnboardingStepStatus.Background) return true
  return false
}

export const useRunInBackgroundSubmitter = (enabled: boolean, onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void): boolean => {
  const [didAttempt, setDidAttempt] = useState(false)
  useEffect(() => {
    try {
      if (enabled) {
        onSubmit()
      }
    } finally {
      setDidAttempt(true)
    }
  }, [enabled, onSubmit])

  return didAttempt
}

interface WithRunInBackgroundHelperProps {
  enabled: boolean
  isSubmitting: boolean
  onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
}
// WithRunInBackgroundHelper will run the submit immediately when enabled.
// It should effectively skip the step. when submits fail, It allows users to fill in the details to patch the form data
export const WithRunInBackgroundHelper: React.FC<WithRunInBackgroundHelperProps> = ({ enabled, isSubmitting, onSubmit, children }) => {
  const didAttempt = useRunInBackgroundSubmitter(enabled, onSubmit)
  if (enabled && (!didAttempt || isSubmitting)) {
    return <Loader loading />
  }

  // ignore rule to satisfy react.FC type
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}
